import {DataTable as PepsicoDsDataTable} from 'pepsico-ds'
import {SIZE} from '@/utils/constants/globalStyle'
import {SELECTION_TYPE} from '@/utils/constants/commonConstants'
import {useEffect, useRef, useState} from 'react'
import {getSelectionType} from '@/components/atoms/DataTable/DataTableUtils'
import {getSelectedRowData} from '@/utils/commons'

/**
 * DataTable component that renders a PepsicoDsDataTable with properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.id=''] - The id of the table. Will be used to generate the id and data-testid.
 * @param {Object[]} props.data - Row data to be rendered in the table. Data keys should match column keys.
 * @param {Object[]} props.columns - Column configuration for the table
 * @param {boolean} [props.isDark=true] - The mode of the table.
 * @param {string} [props.tableHeader=''] - The table heading. Defaults to an empty string.
 * @param {string} props.size - The size of the table. Options: "small" | "medium" | "large".
 * @param {string} props.selection - The selection of the table. Options: "single" | "multiple".
 * @param {number} [props.pageSize=500] - The page size of the table. Defaults to 500.
 * @param {number} [props.pageIndex=0] - The page index of the table. Defaults to 0.
 * @param {Function} [props.onUpdate=() => {}] - Additional effect to be applied when changing pages in the table pagination.
 * @param {string} [props.tableDescription=''] - The table description. Defaults to an empty string.
 * @param {Object[]} props.filters - The filters for the table.
 * @param {Object[]} [props.toolbarActions=[]] - The toolbar actions for the table.
 * @param {Object} [props.rowSelection={}] - Selected row(s) initial state.
 * @param {Function} [props.setRowSelection=() => {}] - Function to update the row selection state
 * @param {Function} [setIsSelectAll=() => {}] - Callback function to update row selection state.
 * @param {string} [props.searchPlaceholder='Search'] - Helper text for the search input. Defaults to 'Search'.
 * @param {boolean} [props.showFilterButton=false] - Show filter button to display filter toolbar.
 * @param {boolean} [props.showFilterToolbar=false] - Prop to show filter toolbar with initial rendering.
 * @param {boolean} [props.isExpandable=false] - Prop to enable expandable rows.
 * @param {Function} [props.renderSubComponent=() => {}] - Function to render the sub component.
 * @param {boolean} [props.showColumnFilters=true] - Prop to show column filters.
 * @param {boolean} [props.enableSorting=true] - Prop to enable sorting.
 * @param {Function} [props.onGlobalSearchChange=() => {}] - Function to handle global search input change.
 * @param {Function} [props.onSortChange=() => {}] - Function to handle column sort change.
 * @param {Function} [props.onColumnSearchChange=() => {}] - Function to handle column search input change.
 * @param {boolean} [props.showGlobalActions=true] - Show global actions bar with search, pagination and filtermenu.
 * @returns {JSX.Element} The rendered PepsicoDsDataTable component.
 */

function DataTable({
  id,
  data,
  columns,
  isDark = true,
  tableHeader = '',
  size = SIZE.SMALL,
  selection = SELECTION_TYPE.MULTIPLE,
  pageSize = 500,
  pageIndex = 0,
  onUpdate = () => {},
  tableDescription = '',
  filters = [],
  toolbarActions = [],
  rowSelection = {},
  setRowSelection = () => {},
  setIsSelectAll = () => {},
  searchPlaceholder = 'Search',
  showFilterButton = true, // TODO: Switch to default false once pepsico-ds package is updated. Current 'true' hides the button and false shows it.
  showFilterToolbar = false,
  setSelectedRowData = () => {},
  isExpandable = false,
  renderSubComponent = () => {},
  showColumnFilters = true,
  enableSorting = true,
  onSortChange = () => {},
  showGlobalActions = true,
  setPageIndex = () => {},
  setSearchArry = () => {},
  setSortingObj = () => {},
  manualPagination = true,
  totalRowsCount = 400,
}) {
  const tableRef = useRef(null)
  const [prevRowSelectedLength, setPrevRowSelectedLength] = useState(0)
  const [isLoadings, setIsLoading] = useState(true)

  useEffect(() => {
    if (Object.keys(rowSelection).length > 0) {
      const selectedRowDataArr = getSelectedRowData(rowSelection, data)
      setSelectedRowData(selectedRowDataArr)
    }
  }, [rowSelection, data, setSelectedRowData])

  const getType = type => {
    if (type == 'text') {
      return 'string'
    } else {
      return type
    }
  }

  const handleRowSelection = selectedRow => {
    setRowSelection(selectedRow)
    const rowSelectionLength = Object.keys(selectedRow).length
    if (prevRowSelectedLength + 1 < rowSelectionLength) {
      setIsSelectAll(true)
    } else if (prevRowSelectedLength < rowSelectionLength) {
      setIsSelectAll(false)
    }
    setPrevRowSelectedLength(rowSelectionLength)
  }

  const handleSorting = sortingState => {
    if (sortingState?.length) {
      const {id, desc} = sortingState[0]
      onSortChange(id, desc ? 'descending' : 'ascending')
    } else {
      onSortChange('', 'clear')
    }
    setSortingObj(sortingState?.length ? sortingState[0] : null)
  }

  // Process columns to add searchPlaceholder if not already present
  const processedColumns =
    columns?.map(column => {
      // If column doesn't have a searchPlaceholder, use the global one
      if (!column.searchPlaceholder) {
        return {
          ...column,
          searchPlaceholder: `Search by ${column.header?.toLowerCase() || column.columnKey}`,
        }
      }
      return column
    }) || []
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500) // 1000 ms = 1 second

    // Optional: clear timeout if component unmounts
    return () => clearTimeout(timer)
  }, [])
  return (
    <div className="rw-datatable" ref={tableRef}>
      <PepsicoDsDataTable
        isLoading={isLoadings}
        id={`data_table` + (id ? `_${id}` : '')}
        data-testid={`data_table` + (id ? `_${id}` : '')}
        columns={processedColumns}
        data={data || []}
        isDark={isDark}
        manualPagination={manualPagination}
        totalRowsCount={totalRowsCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        tableHeader={tableHeader}
        tableDescription={tableDescription}
        selection={getSelectionType(selection)}
        size={size}
        filters={filters}
        onUpdate={onUpdate}
        toolbarActions={toolbarActions}
        rowSelection={rowSelection}
        searchPlaceholder={searchPlaceholder}
        showFilterButton={showFilterButton}
        showFilterToolbar={showFilterToolbar}
        isExpandable={isExpandable}
        renderSubComponent={renderSubComponent}
        showColumnFilters={showColumnFilters}
        enableSorting={enableSorting}
        showGlobalActions={showGlobalActions}
        rowSelectionStateUpdater={selectedRow => {
          handleRowSelection(selectedRow)
        }}
        onPageIndexChange={e => {
          setPageIndex(e)
        }}
        onColumnFiltersChange={(value, type, name) => {
          setSearchArry({field: name, value, type: getType(type)})
        }}
        sortingHandler={handleSorting}
      />
    </div>
  )
}

export default DataTable
